import { LoaderParserPriority } from '../../assets/loader/parsers/LoaderParser';
import { createTexture } from '../../assets/loader/parsers/textures/utils/createTexture';
import { checkExtension } from '../../assets/utils/checkExtension';
import { ExtensionType } from '../../extensions/Extensions';
import { CompressedSource } from '../../rendering/renderers/shared/texture/sources/CompressedSource';
import { getSupportedTextureFormats } from '../../rendering/renderers/shared/texture/utils/getSupportedTextureFormats';
import { getResolutionOfUrl } from '../../utils/network/getResolutionOfUrl';
import { loadKTX2onWorker } from './worker/loadKTX2onWorker';

import type { Loader } from '../../assets/loader/Loader';
import type { LoaderParser } from '../../assets/loader/parsers/LoaderParser';
import type { ResolvedAsset } from '../../assets/types';
import type { TextureSourceOptions } from '../../rendering/renderers/shared/texture/sources/TextureSource';
import type { Texture } from '../../rendering/renderers/shared/texture/Texture';

/**
 * Loader parser for KTX2 textures.
 * This parser loads KTX2 textures using a web worker for transcoding.
 * It supports both single and multiple textures.
 * @category assets
 * @advanced
 */
export const loadKTX2 = {
    extension: {
        type: ExtensionType.LoadParser,
        priority: LoaderParserPriority.High,
        name: 'loadKTX2',
    },

    /** used for deprecation purposes */
    name: 'loadKTX2',
    id: 'ktx2',

    test(url: string): boolean
    {
        return checkExtension(url, '.ktx2');
    },

    async load(url: string, asset: ResolvedAsset, loader: Loader): Promise<Texture | Texture[]>
    {
        const supportedTextures = await getSupportedTextureFormats();

        const textureOptions = await loadKTX2onWorker(url, supportedTextures);

        const compressedTextureSource = new CompressedSource({
            ...textureOptions,
            resolution: asset.data?.resolution || getResolutionOfUrl(url),
        });

        return createTexture(compressedTextureSource, loader, url);
    },

    async unload(texture: Texture | Texture[]): Promise<void>
    {
        if (Array.isArray(texture))
        {
            texture.forEach((t) => t.destroy(true));
        }
        else
        {
            texture.destroy(true);
        }
    }

} satisfies LoaderParser<Texture | Texture[], TextureSourceOptions>;

